<script setup lang="ts">
import type { Collections } from '@nuxt/content'

const { locale } = useI18n()

const { data: faq } = await useAsyncData('faq-' + locale.value, async () => {
  const collection = ('faq_' + locale.value) as keyof Collections
  return await queryCollection(collection).first() as Collections['faq_en'] | Collections['faq_fr']
}, {
  watch: [locale],
})

interface FaqItem {
  label: string
  content: string
}

const items = computed<FaqItem[]>(() => {
  return faq.value?.items || []
})

// SEO: Add FAQPage Schema
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: computed(() => JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': items.value.map((item: FaqItem) => ({
          '@type': 'Question',
          'name': item.label,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': item.content,
          },
        })),
      })),
    },
  ],
})
</script>

<template>
  <section
    v-if="faq"
    class="mx-auto grid w-full max-w-6xl items-start gap-8 text-left lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-16"
  >
    <div class="lg:sticky lg:top-24">
      <h2 class="text-balance text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl">
        {{ faq.subtitle }}
      </h2>
      <p class="mt-5 text-white/60">
        {{ $t("global.faq_more") }}
      </p>
      <NuxtLinkLocale
        to="/contact"
        class="group mt-2 inline-flex items-center gap-2 font-medium text-white"
      >
        {{ $t("global.contact") }}
        <UIcon
          name="heroicons:arrow-right"
          class="size-4 transition-transform duration-300 group-hover:translate-x-1"
        />
      </NuxtLinkLocale>
    </div>

    <UAccordion
      :unmount-on-hide="false"
      default-value="0"
      trailing-icon="lucide:plus"
      :items="items"
      :ui="{
        item: 'border-b border-white/10 first:border-t last:border-b',
        trigger: 'cursor-pointer gap-6 py-5',
        label: 'text-left text-base font-medium text-white sm:text-lg',
        trailingIcon: 'size-5 text-white/60 group-data-[state=open]:rotate-45',
        body: 'pb-6 pr-10 text-base leading-relaxed text-white/60',
      }"
    >
      <template #body="{ item: _item }">
        <MDC
          :value="_item.content || ''"
          unwrap="p"
        />
      </template>
    </UAccordion>
  </section>
</template>
